import { cn } from '@sixam/ui/utils';
import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import { signInWithOAuth, type OAuthProvider } from '@sixam/supabase/auth';
import { supabase } from '@sixam/supabase/client';
import * as React from 'react';
import { Image, Platform, View } from 'react-native';
import { router } from 'expo-router';
import { authDestination } from '@/lib/auth-return';
import { recordTermsAcceptance } from '@/lib/community-terms-client';
import { NativeAppleSignInButton } from '@/components/native-apple-sign-in-button';
import { authedFetch } from '@/lib/authed-fetch';

const SOCIAL_CONNECTION_STRATEGIES: {
  type: OAuthProvider;
  source: { uri: string };
  useTint: boolean;
}[] = [
  {
    type: 'apple',
    source: { uri: 'https://img.clerk.com/static/apple.png?width=160' },
    useTint: true,
  },
  {
    type: 'google',
    source: { uri: 'https://img.clerk.com/static/google.png?width=160' },
    useTint: false,
  },
  {
    type: 'github',
    source: { uri: 'https://img.clerk.com/static/github.png?width=160' },
    useTint: true,
  },
];

function getNativeTintColor(useTint: boolean) {
  if (!useTint) {
    return undefined;
  }

  return 'white';
}

function getProviderLabel(provider: OAuthProvider) {
  if (provider === 'apple') {
    return 'Apple';
  }

  if (provider === 'google') {
    return 'Google';
  }

  return 'GitHub';
}

type AppleAuthenticationModule = typeof import('expo-apple-authentication');
type AppleCredential = Awaited<ReturnType<AppleAuthenticationModule['signInAsync']>>;

async function retainAppleAuthorization(authorizationCode: string | null) {
  if (!authorizationCode) {
    throw new Error('Apple did not return the authorization needed for account deletion.');
  }
  const response = await authedFetch('/api/auth/apple/credential', {
    body: JSON.stringify({ authorizationCode }),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });
  if (response.ok) return;

  const payload = (await response.json().catch(() => null)) as { error?: string } | null;
  throw new Error(payload?.error ?? 'Could not prepare Apple account deletion.');
}

async function saveAppleName(
  AppleAuthentication: AppleAuthenticationModule,
  credential: AppleCredential
) {
  const fullName = credential.fullName
    ? AppleAuthentication.formatFullName(credential.fullName)
    : '';
  if (!fullName) return;

  const { error } = await supabase.auth.updateUser({
    data: {
      family_name: credential.fullName?.familyName,
      full_name: fullName,
      given_name: credential.fullName?.givenName,
    },
  });
  if (error) throw error;
}

async function authenticateWithNativeApple() {
  const AppleAuthentication = await import('expo-apple-authentication');
  const credential = await AppleAuthentication.signInAsync({
    requestedScopes: [
      AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
      AppleAuthentication.AppleAuthenticationScope.EMAIL,
    ],
  });

  if (!credential.identityToken) {
    throw new Error('Apple did not return an identity token. Please try again.');
  }
  const { error } = await supabase.auth.signInWithIdToken({
    provider: 'apple',
    token: credential.identityToken,
  });
  if (error) throw error;

  await retainAppleAuthorization(credential.authorizationCode);
  await saveAppleName(AppleAuthentication, credential);
}

export function SocialConnections({
  returnTo,
  termsAccepted,
}: Readonly<{ returnTo?: string | string[]; termsAccepted: boolean }>) {
  const [error, setError] = React.useState<string | null>(null);
  const [loadingProvider, setLoadingProvider] = React.useState<OAuthProvider | null>(null);

  async function finishNativeSignIn() {
    try {
      await recordTermsAcceptance();
    } catch (acceptanceError) {
      await supabase.auth.signOut({ scope: 'local' });
      throw acceptanceError;
    }
    router.replace(authDestination(returnTo));
  }

  async function onSocialSignIn(provider: OAuthProvider) {
    if (!termsAccepted) {
      setError('Please agree to the community terms before signing in.');
      return;
    }
    setLoadingProvider(provider);
    setError(null);

    try {
      const result = await signInWithOAuth(provider);
      if (Platform.OS !== 'web' && result.type === 'success') {
        await finishNativeSignIn();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Social sign-in failed');
    }

    setLoadingProvider(null);
  }

  async function onNativeAppleSignIn() {
    if (!termsAccepted) {
      setError('Please agree to the community terms before signing in.');
      return;
    }
    setLoadingProvider('apple');
    setError(null);

    try {
      await authenticateWithNativeApple();
      await finishNativeSignIn();
    } catch (signInError) {
      const isCancelled =
        signInError instanceof Error &&
        'code' in signInError &&
        signInError.code === 'ERR_REQUEST_CANCELED';
      if (!isCancelled) {
        await supabase.auth.signOut({ scope: 'local' });
        setError(signInError instanceof Error ? signInError.message : 'Apple sign-in failed');
      }
    } finally {
      setLoadingProvider(null);
    }
  }

  return (
    <View className="gap-2">
      {Platform.OS === 'ios' ? (
        <NativeAppleSignInButton
          disabled={!termsAccepted || loadingProvider !== null}
          onPress={() => void onNativeAppleSignIn()}
        />
      ) : null}
      <View className="gap-2 sm:flex-row sm:gap-3">
        {SOCIAL_CONNECTION_STRATEGIES.filter(
          (strategy) => Platform.OS !== 'ios' || strategy.type !== 'apple'
        ).map((strategy) => {
          const tintColor = Platform.select({
            native: getNativeTintColor(strategy.useTint),
          });
          const label = getProviderLabel(strategy.type);

          return (
            <Button
              key={strategy.type}
              variant="outline"
              size="default"
              className="h-11 border-white/20 bg-black/20 sm:flex-1"
              disabled={!termsAccepted || loadingProvider !== null}
              onPress={() => onSocialSignIn(strategy.type)}
            >
              <Image
                className={cn('size-4', strategy.useTint && Platform.select({ web: 'invert' }))}
                tintColor={tintColor}
                source={strategy.source}
              />
              <Text className="text-[11px]">
                {loadingProvider === strategy.type ? 'Opening...' : label}
              </Text>
            </Button>
          );
        })}
      </View>
      {error ? (
        <Text className="text-center text-sm text-destructive" selectable>
          {error}
        </Text>
      ) : null}
    </View>
  );
}
